/**
 * Persistencia del `sessionId` para sobrevivir un F5 / recarga de la página.
 *
 * A diferencia de la auto-reconexión (restart/blip con la página abierta, donde
 * el `sessionId` vive en memoria en `sessionIdRef`), un F5 borra TODO lo
 * in-memory. Para que "se me colgó → F5 → sigo la misma charla" funcione,
 * guardamos `{id, ts}` en `localStorage`: al montar, si hay uno FRESCO (dentro
 * del TTL), el hook lo manda como `reconnectSessionId` y el backend reattachea.
 *
 * - `ts` se refresca mientras la charla está viva (cada transición de turno), no
 *   sólo al arrancar: así un F5 tras 20 min de charla sigue siendo fresco.
 * - En un cierre DELIBERADO (`stop()`) se limpia — esa charla terminó a
 *   propósito, no hay que reattachearla.
 * - Best-effort: si `localStorage` no está (SSR, modo privado, cuota), no rompe
 *   nada — degrada a "arrancar de cero" (el comportamiento previo).
 *
 * El backend es el gate autoritativo de elegibilidad (ventana de gracia sobre
 * `ended_at`). El TTL de acá sólo evita INTENTAR un reattach obviamente viejo
 * (ej. reabrir el tab al otro día → mejor arrancar limpio que resumir algo rancio).
 */

const KEY_PREFIX = 'orborix:voice:reattach:';

/**
 * TTL del id persistido (ms). Igual a `RECONNECT_GRACE_SECONDS` del backend
 * (120s): gaps más largos los cubre "Continuar conversación" desde el historial.
 */
export const REATTACH_PERSIST_TTL_MS = 120_000;

interface PersistedReattach {
  id: string;
  ts: number;
}

function storageKey(moduleSlug: string | undefined): string {
  return `${KEY_PREFIX}${moduleSlug ?? 'default'}`;
}

/** Guarda/refresca `{id, ts:now}` para el módulo. Best-effort. */
export function persistReattachSession(
  moduleSlug: string | undefined,
  sessionId: string,
  now: number = Date.now(),
): void {
  try {
    localStorage.setItem(storageKey(moduleSlug), JSON.stringify({ id: sessionId, ts: now }));
  } catch {
    /* sin localStorage / cuota — best-effort */
  }
}

/** Borra el id persistido del módulo. Best-effort. */
export function clearReattachSession(moduleSlug: string | undefined): void {
  try {
    localStorage.removeItem(storageKey(moduleSlug));
  } catch {
    /* ignore */
  }
}

/**
 * Devuelve el id persistido si es FRESCO (dentro del TTL); si está vencido o es
 * inválido, lo limpia y devuelve `undefined`. `undefined` = arrancar de cero.
 */
export function readFreshReattachSession(
  moduleSlug: string | undefined,
  ttlMs: number = REATTACH_PERSIST_TTL_MS,
  now: number = Date.now(),
): string | undefined {
  try {
    const raw = localStorage.getItem(storageKey(moduleSlug));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<PersistedReattach>;
    if (typeof parsed.id !== 'string' || typeof parsed.ts !== 'number') {
      clearReattachSession(moduleSlug);
      return undefined;
    }
    if (now - parsed.ts > ttlMs) {
      clearReattachSession(moduleSlug); // vencido
      return undefined;
    }
    return parsed.id;
  } catch {
    return undefined;
  }
}
